import React from 'react';
import { Container, Grid, Box, Typography, Card, CardContent } from '@mui/material';
import { CheckCircle, AutoStories } from '@mui/icons-material';

export const ApproachSection: React.FC = () => {
    const approaches = [
        'Small Group Classes – individual attention for every student',
        'Personalised Learning Plans – focus on strengths and improvement areas',
        'Regular Assessments – continuous progress tracking',
        'Mock Exams – real exam conditions to reduce stress',
        'Supportive Environment – encouragement, not pressure',
        'Proven Resources – quality materials and practice papers',
    ];

    return (
        <Box
            id="approach"
            sx={{
                py: { xs: 8, md: 12 },
            }}
        >
            <Container maxWidth="lg">
                <Typography
                    variant="h2"
                    sx={{
                        mb: 2,
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #5b4cdb 0%, #1e293b 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    Our Teaching Approach
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        mb: 6,
                        textAlign: 'center',
                        color: 'text.secondary',
                        maxWidth: 700,
                        mx: 'auto',
                        lineHeight: 1.8,
                    }}
                >
                    A balanced blend of expert instruction, structured practice, and personalized support
                    designed to build confidence and achieve results.
                </Typography>

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            background: 'linear-gradient(135deg, #5b4cdb 0%, #06b6d4 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <CheckCircle sx={{ color: '#fff', fontSize: 28 }} />
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                        What We Offer
                                    </Typography>
                                </Box>
                                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                                    {approaches.map((item, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                mb: 2.5,
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <CheckCircle sx={{ color: '#5b4cdb', fontSize: 24, flexShrink: 0, mt: 0.5 }} />
                                            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                                {item}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            background: 'linear-gradient(135deg, #06b6d4 0%, #5b4cdb 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <AutoStories sx={{ color: '#fff', fontSize: 28 }} />
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                        How We Help Students Improve
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 3 }}>
                                    We teach core concepts clearly, then build skills with structured practice and
                                    timed questions. Students learn exam techniques, strengthen weak areas, and gain
                                    confidence through mock exams.
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                                    Our step-by-step approach ensures steady progress. We identify gaps early,
                                    provide targeted support, and celebrate milestones along the way.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Learning Cycle Info */}
                <Box
                    sx={{
                        mt: 6,
                        p: 4,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(91, 76, 219, 0.05) 0%, rgba(6, 182, 212, 0.05) 100%)',
                        border: '1px solid rgba(91, 76, 219, 0.15)',
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                        Our Learning Cycle
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, textAlign: 'center' }}>
                        <strong>Teach</strong> → <strong>Practice</strong> → <strong>Assess</strong> → <strong>Review</strong> → <strong>Improve</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, textAlign: 'center', mt: 2 }}>
                        This continuous cycle ensures mastery of concepts and sustained improvement throughout the course.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};
